// Web-Push (native OS notification) helpers for the browser / installed PWA.
import api from './api'

export const isPushSupported = () =>
  typeof window !== 'undefined' &&
  'serviceWorker' in navigator &&
  'PushManager' in window &&
  'Notification' in window

export const currentPermission = () =>
  (typeof Notification !== 'undefined' ? Notification.permission : 'denied')

// VAPID public key (base64url) → Uint8Array for pushManager.subscribe.
function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4)
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/')
  const raw = atob(base64)
  const out = new Uint8Array(raw.length)
  for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i)
  return out
}

// Is this device currently subscribed?
export const isSubscribed = async () => {
  if (!isPushSupported()) return false
  try {
    const reg = await navigator.serviceWorker.ready
    const sub = await reg.pushManager.getSubscription()
    return !!sub
  } catch { return false }
}

// Request permission, subscribe, and register the subscription with the backend.
// Returns { ok: true } or { ok: false, reason }.
export const enablePush = async () => {
  if (!isPushSupported()) return { ok: false, reason: 'unsupported' }
  try {
    const { data } = await api.get('/push/vapid-public-key')
    const key = data?.key
    if (!key) return { ok: false, reason: 'not-configured' }

    const permission = await Notification.requestPermission()
    if (permission !== 'granted') return { ok: false, reason: 'denied' }

    const reg = await navigator.serviceWorker.ready
    let sub = await reg.pushManager.getSubscription()
    if (!sub) {
      sub = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(key),
      })
    }
    const json = sub.toJSON()
    await api.post('/push/subscribe', { endpoint: json.endpoint, keys: json.keys })
    return { ok: true }
  } catch (e) {
    return { ok: false, reason: e?.message || 'error' }
  }
}

// Unsubscribe this device and tell the backend to forget it.
export const disablePush = async () => {
  if (!isPushSupported()) return { ok: false, reason: 'unsupported' }
  try {
    const reg = await navigator.serviceWorker.ready
    const sub = await reg.pushManager.getSubscription()
    if (sub) {
      const endpoint = sub.endpoint
      try { await sub.unsubscribe() } catch { /* ignore */ }
      try { await api.post('/push/unsubscribe', { endpoint }) } catch { /* ignore */ }
    }
    return { ok: true }
  } catch (e) {
    return { ok: false, reason: e?.message || 'error' }
  }
}

// ── Per-user preference ──────────────────────────────────────────────────────
// The toggle is PER USER (keyed by their id), defaulting ON — so it never clashes when
// several accounts (consultant / admin / PM) are used on the same browser, and a fresh
// user starts with notifications wanted. The actual OS subscription is per-browser, so we
// (re)point it to whichever user is currently active and has it on.
const prefKey = (userId) => `push-enabled:${userId ?? 'anon'}`
export const getPushPref = (userId) => {
  try { return localStorage.getItem(prefKey(userId)) !== '0' } catch { return true } // default ON
}
export const setPushPref = (userId, on) => {
  try { localStorage.setItem(prefKey(userId), on ? '1' : '0') } catch { /* ignore */ }
}

// On login, if THIS user wants push (their per-user pref, default on) and the browser has
// already granted permission, make sure the subscription is registered to them. Respects a
// user who turned it off, and never prompts (silent no-op if permission not yet granted).
export const resubscribeIfGranted = async (userId) => {
  if (!isPushSupported() || currentPermission() !== 'granted') return
  if (!getPushPref(userId)) return
  try { await enablePush() } catch { /* ignore */ }
}
